import {pickPhysicalUdoAdditionalColumnValues} from "~/utilities/capabilityUdoTemplateColumns";
import type {UserDefinedObjectTemplateAdditionalColumn as UdoColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {useUserDefinedObjectFieldValidation} from "~/components/userDefinedObjectAdditionalColumnField";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import {useCapabilityUdoColumns} from "~/contexts/workspaceContext";
import CalendarIcon from "@atlaskit/icon/core/calendar";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import LinkExternalIcon from "@atlaskit/icon/core/link-external";
import NoteIcon from "@atlaskit/icon/core/note";
import TagIcon from "@atlaskit/icon/core/tag";
import {token} from "@atlaskit/tokens";
import {useNavigate} from "@tanstack/react-router";
import type {NewCoreIconProps, NewIconProps} from "@atlaskit/icon/dist/types/types";
import {useQueryClient} from "@tanstack/react-query";
import {useEffect, useMemo, useRef, useState, type JSX} from "react";
import {AnankeTempCollapsibleSectionWithIcon} from "~/components/anankeTemp/anankeTempCollapsibleSectionWithIcon";
import {AnankeTempFormCollapsibleSection} from "~/components/anankeTemp/anankeTempFormCollapsibleSection";
import {EventDetailsEditColumns, type EventColumnGroupWithColumns} from "~/components/eventDetails/eventDetailColumns";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {configJourneyProfileHref} from "~/workspaceConfig/configHref";
import {
    EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID,
    EmotoradCallStatusEnum,
    EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
    EmotoradEventTypes,
    isEmotoradWorkspace,
    EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID,
    EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID,
    EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID,
    EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT__WALKINS__CUSTOMER_STATE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID,
    EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
    emotoradEnquiryDigitalLeadStatuses,
    emotoradLeadEventStatuses,
} from "~/domainModels/workspaceSpecific/emotorad";
import {AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID, AmaronEventTypes, isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {DuluxEventTypes, isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {isDuluxScheduledCallEvent} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/ottopilot/_ottopilotScaffold/events/$eventTemplateId/index/-dulux-edit-panel";
import {DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/dulux/Events";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {getEventEditPanelColumnIsEditable} from "~/domainModels/eventEditPanelConstraints";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {eventContainerEditPanelIconsMap} from "~/domainModels/eventContainerPanelIcons";
import type {EventContainerTemplate as JourneysModuleConfiguration} from "~/domainModels/workspaceModules/eventContainersModule";
import {AnankeAvatar} from "@vesta/ananke/components/anankeAvatar";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/utilities/apis/api";
import {useBtlActivities} from "~/contexts/workspaceBtlActivitiesContext";
import {useOrganizationalUnitTemplates} from "~/contexts/workspaceContext";
import {useOrganizationalUnits} from "~/contexts/organizationalUnitsContext";
import {useProductCatalogue} from "~/contexts/productCatalogueContext";
import {useWorkspaceUserTemplates as useUserTemplates} from "~/contexts/workspaceContext";
import {useUsers} from "~/contexts/workspaceUsersContext";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {invalidateConfigDrivenWorkspaceQueries} from "~/workspaceConfig/invalidateConfigDrivenWorkspaceQueries";
import {useReportPanelDirty} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementUnsavedChangesContext";
import {isFormValueDirty} from "~/utilities/isFormValueDirty";
import {amaronUpdateEventAction} from "~/utilities/apis/amaronActionsApi";
import {duluxEditScheduledCallAction, duluxEventEditPanelSaveAction} from "~/utilities/apis/duluxActionsApi";
import {duluxScheduleRawToApiString} from "~/utilities/eventContainerTriggerEvent";
import {getEventsWithEventContainers, updateEvent} from "~/utilities/apis/api";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import {getGeographyDetailsByPincode, type GeographyDetailsResult} from "~/utilities/apis/geography";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {
    applyEmotoradTestRideDateTimeSplitToEventApiPayload,
    combineEmotoradTestRidePickerDateAndSlotHhMmToUtcIso,
    splitEmotoradTestRideStoredInstantToPickerDateAndSlot,
} from "~/workspaceConfig/hostedForms/shared/emotorad/emotoradTestrideTimeSlots";
import {
    formatDateAndTime,
    getUserDisplayName,
    schedulePickerOrLegacyToUtcIso,
} from "~/utilities/utilities";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";
import {resolveColumnValue, type ColumnValueDeps} from "./eventViewPanel";
import {ListNotesPanelBody} from "~/components/notes/listNotesSidePanel";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {getEventContainerOrganizationalUnitId} from "~/utilities/ottopilotSemanticColumns";

type EventColumnGroupWithOptionalIcon = EventColumnGroupWithColumns & {
    icon?: (props: NewCoreIconProps) => JSX.Element;
    iconColor?: NewIconProps["color"];
    iconBackgroundColor?: string;
    initialIsOpen?: boolean;
    className?: string;
};

/** Drops keys that are not real additional columns (e.g. synthetic UI-only ids merged into row state). */
type EmotoradCustomerPincodeGeoTargets = {
    event: Partial<{state: Uuid; city: Uuid; zone: Uuid}>;
    journey: Partial<{state: Uuid; city: Uuid; zone: Uuid}>;
    /** When the geography API omits zone, clear journey zone only for journey-level customer pincode. */
    clearJourneyZoneWhenZoneUnknown: boolean;
};

function getEmotoradCustomerPincodeGeoTargets(columnId: string): EmotoradCustomerPincodeGeoTargets | null {
    const journeyZoneOnly = {zone: EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID} as const;
    const table: Record<string, EmotoradCustomerPincodeGeoTargets> = {
        [String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID)]: {
            event: {},
            journey: {
                state: EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID,
                city: EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID,
                zone: EMOTORAD__EVENT_CONTAINER__CUSTOMER_ZONE__COLUMN_ID,
            },
            clearJourneyZoneWhenZoneUnknown: true,
        },
        [String(EMOTORAD__EVENT__CALLS__CUSTOMER_PINCODE__COLUMN_ID)]: {
            event: {
                state: EMOTORAD__EVENT__CALLS__CUSTOMER_STATE__COLUMN_ID,
                city: EMOTORAD__EVENT__CALLS__CUSTOMER_CITY__COLUMN_ID,
            },
            journey: {...journeyZoneOnly},
            clearJourneyZoneWhenZoneUnknown: false,
        },
        [String(EMOTORAD__EVENT__SALES__CUSTOMER_PINCODE__COLUMN_ID)]: {
            event: {
                state: EMOTORAD__EVENT__SALES__CUSTOMER_STATE__COLUMN_ID,
                city: EMOTORAD__EVENT__SALES__CUSTOMER_CITY__COLUMN_ID,
            },
            journey: {...journeyZoneOnly},
            clearJourneyZoneWhenZoneUnknown: false,
        },
        [String(EMOTORAD__EVENT__WALKINS__CUSTOMER_PINCODE__COLUMN_ID)]: {
            event: {
                state: EMOTORAD__EVENT__WALKINS__CUSTOMER_STATE__COLUMN_ID,
                city: EMOTORAD__EVENT__WALKINS__CUSTOMER_CITY__COLUMN_ID,
            },
            journey: {...journeyZoneOnly},
            clearJourneyZoneWhenZoneUnknown: false,
        },
        [String(EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID)]: {
            event: {
                state: EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID,
                city: EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID,
            },
            journey: {...journeyZoneOnly},
            clearJourneyZoneWhenZoneUnknown: false,
        },
        [String(EMOTORAD__EVENT__ENQUIRY__CUSTOMER_PINCODE__COLUMN_ID)]: {
            event: {
                state: EMOTORAD__EVENT__ENQUIRY__CUSTOMER_STATE__COLUMN_ID,
                city: EMOTORAD__EVENT__ENQUIRY__CUSTOMER_CITY__COLUMN_ID,
            },
            journey: {...journeyZoneOnly},
            clearJourneyZoneWhenZoneUnknown: false,
        },
    };
    return table[columnId] ?? null;
}

function buildEmotoradGeographyPatches(
    targets: EmotoradCustomerPincodeGeoTargets,
    details: GeographyDetailsResult | null,
): {event: Record<Uuid, unknown>; journey: Record<Uuid, unknown>} {
    const event: Record<Uuid, unknown> = {};
    const journey: Record<Uuid, unknown> = {};

    const applyNullClear = () => {
        for (const key of ["state", "city", "zone"] as const) {
            const e = targets.event[key];
            if (e !== undefined) {
                event[e] = null;
            }
            const j = targets.journey[key];
            if (j !== undefined) {
                journey[j] = null;
            }
        }
    };

    if (details === null) {
        applyNullClear();
        return {event, journey};
    }

    if (targets.event.state !== undefined) {
        event[targets.event.state] = details.state ?? null;
    }
    if (targets.event.city !== undefined) {
        event[targets.event.city] = details.city ?? null;
    }
    if (targets.event.zone !== undefined) {
        event[targets.event.zone] = details.zone ?? null;
    }

    if (targets.journey.state !== undefined) {
        journey[targets.journey.state] = details.state ?? null;
    }
    if (targets.journey.city !== undefined) {
        journey[targets.journey.city] = details.city ?? null;
    }

    if (targets.journey.zone !== undefined) {
        if (details.zone !== null) {
            journey[targets.journey.zone] = details.zone;
        } else if (targets.clearJourneyZoneWhenZoneUnknown === true) {
            journey[targets.journey.zone] = null;
        }
    }

    return {event, journey};
}

function pickAdditionalColumnValuesForKnownColumns(values: Record<Uuid, unknown>, columns: ReadonlyArray<UdoColumn>): Record<Uuid, unknown> {
    return pickPhysicalUdoAdditionalColumnValues(columns, values);
}

function isEditPanelRequiredValueEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === "string" && value.trim() === "") {
        return true;
    }
    if (typeof value === "number" && Number.isNaN(value)) {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

function getEditPanelColumnRawValue(column: EventColumnGroupWithOptionalIcon["columns"][number], eventWithDiff: EventWithEventContainer): unknown {
    if (column.columnType === "OttopilotEventContainer") {
        if (column.isAdditionalColumn === true) {
            return eventWithDiff.eventContainer.additionalColumnValues[column.columnId as Uuid];
        }
        return (eventWithDiff.eventContainer as unknown as Record<string, unknown>)[column.columnId as string];
    }
    if (column.isAdditionalColumn === true) {
        return eventWithDiff.additionalColumnValues[column.columnId as Uuid];
    }
    return (eventWithDiff as unknown as Record<string, unknown>)[column.columnId as string];
}

function collectEmptyRequiredEditableColumnConfigIds(columnGrouping: Array<EventColumnGroupWithOptionalIcon>, eventWithDiff: EventWithEventContainer): Set<string> {
    const invalid = new Set<string>();
    for (const group of columnGrouping) {
        for (const col of group.columns) {
            if (col.isRequired !== true || col.isEditable !== true) {
                continue;
            }
            const raw = getEditPanelColumnRawValue(col, eventWithDiff);
            if (isEditPanelRequiredValueEmpty(raw) === true) {
                invalid.add(String(col.id));
            }
        }
    }
    return invalid;
}

function getEditPanelColumnGroupingFromConfig(getAdditionalColumns: CapabilityUdoColumnResolver,
    eventTemplate: OttopilotEventTemplate,
    event: EventWithEventContainer,
    isExpanded: boolean,
    useLineSeparatedSections: boolean,
): Array<EventColumnGroupWithOptionalIcon> {
    const expandedClassName =
        useLineSeparatedSections === true ? "py-ds-space-100 px-ds-space-200" : "border border-ds-border rounded-ds-border-radius-200 p-ds-space-200";
    const collapsedClassName = "border-ds-border py-ds-space-100 px-ds-space-200";
    const sectionClassName = `border-t border-ds-border ${isExpanded ? expandedClassName : collapsedClassName}`;

    const editPanel = eventTemplate.configuration.eventPage.editPanel;

    if (editPanel === undefined || editPanel.columnGrouping.length === 0) {
        return [
            {
                id: `${eventTemplate.id}-fallback-edit` as Uuid,
                displayName: "Event details",
                icon: InformationCircleIcon,
                iconColor: "var(--ds-icon-accent-blue)" as NewIconProps["color"],
                iconBackgroundColor: "var(--ds-background-accent-blue-subtlest)",
                initialIsOpen: true,
                className: sectionClassName,
                columns: getAdditionalColumns(eventTemplate).map((col) => ({
                    id: col.id,
                    columnId: col.id,
                    columnType: "OttopilotEvent" as const,
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                    isEditable: true,
                    label: col.displayName,
                })),
            },
        ];
    }

    const globalConstraints = editPanel.globalConstraints;

    return editPanel.columnGrouping.map((group, index) => {
        const icon = group.iconId !== undefined ? eventContainerEditPanelIconsMap[group.iconId] : undefined;
        const className =
            group.className ??
            (index === 0
                ? sectionClassName
                : isExpanded
                  ? useLineSeparatedSections === true
                      ? `border-t border-ds-border ${expandedClassName}`
                      : expandedClassName
                  : `border-t ${collapsedClassName}`);
        return {
            id: group.id,
            displayName: group.displayName,
            icon,
            iconColor: group.iconColor as NewIconProps["color"] | undefined,
            iconBackgroundColor: group.iconBackgroundColor,
            initialIsOpen: group.initialIsOpen ?? true,
            className,
            columns: group.columns.map((col) => ({
                id: col.id,
                columnId: col.columnId,
                columnType: col.columnType,
                eventTemplateId: col.columnType === "OttopilotEvent" ? col.eventTemplateId : undefined,
                isAdditionalColumn: col.isAdditionalColumn,
                isEditable: getEventEditPanelColumnIsEditable(event, col, undefined, globalConstraints),
                isRequired: col.isRequired === true,
                label: col.label,
            })),
        };
    });
}

export function EventEditPanel({
    row,
    isExpanded,
    eventTemplate,
    journeysModuleConfiguration,
    eventTemplates,
    notesEventContainerSourceLabel,
    onSuccess,
    queryKeyPrefix,
}: {
    row: EventWithEventContainer;
    isExpanded: boolean;
    eventTemplate: OttopilotEventTemplate;
    journeysModuleConfiguration: JourneysModuleConfiguration;
    eventTemplates: Array<OttopilotEventTemplate>;
    notesEventContainerSourceLabel?: string;
    onSuccess?: (data: EventWithEventContainer) => void;
    queryKeyPrefix?: Array<string | null>;
}) {
    const {fieldsRef, validateFields} = useUserDefinedObjectFieldValidation();
    const getAdditionalColumns = useCapabilityUdoColumns();
    const resolvedNotesEventContainerSourceLabel =
        notesEventContainerSourceLabel ??
        journeysModuleConfiguration.configuration.moduleDisplayNameVariants.singularSentenceCase;
    const {
        workspace,
        workspaceCapabilities,
        platformUserDetails,
        userDefinedObjectOptionsByTemplateId,
    } = useWorkspace();
    const {showFlag} = useAnankeFlags();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {heimdallUser} = useWorkspaceUser();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const users = useUsers();
    const userTemplates = useUserTemplates();
    const organizationalUnits = useOrganizationalUnits();
    const organizationalUnitTemplates = useOrganizationalUnitTemplates();
    const btlActivities = useBtlActivities();
    const products = useProductCatalogue();
    const platformUserDetailsByUserId = useMemo(
        () => new Map(platformUserDetails.map((platformUserDetail) => [platformUserDetail.userId, platformUserDetail])),
        [platformUserDetails],
    );
    const isEmotoradCallsPanel = isEmotoradWorkspace(workspace) === true && eventTemplate.id === (EmotoradEventTypes.call as Uuid);
    const isEmotoradWalkinPanel = isEmotoradWorkspace(workspace) === true && eventTemplate.id === (EmotoradEventTypes.walkin as Uuid);
    const isEmotoradLeadPanel = isEmotoradWorkspace(workspace) === true && eventTemplate.id === (EmotoradEventTypes.lead as Uuid);
    const isEmotoradTestridePanel = isEmotoradWorkspace(workspace) === true && eventTemplate.id === (EmotoradEventTypes.testride as Uuid);
    const isEmotoradSalesPanel = isEmotoradWorkspace(workspace) === true && eventTemplate.id === (EmotoradEventTypes.sales as Uuid);
    const isEmotoradInqueueStyledEventPanel =
        isEmotoradCallsPanel || isEmotoradSalesPanel || isEmotoradWalkinPanel || isEmotoradLeadPanel || isEmotoradTestridePanel;
    const isAmaronScaffoldStyledEventPanel =
        isAmaronWorkspace(workspaceCapabilities) === true &&
        (eventTemplate.id === (AmaronEventTypes.Calls as Uuid) ||
            eventTemplate.id === (AmaronEventTypes.Enquiry as Uuid) ||
            eventTemplate.id === (AmaronEventTypes.Service as Uuid));
    const isDuluxScaffoldStyledEventPanel =
        isDuluxWorkspace(workspaceCapabilities) === true &&
        (eventTemplate.id === (DuluxEventTypes.Calls as Uuid) ||
            eventTemplate.id === (DuluxEventTypes.Appointments as Uuid) ||
            eventTemplate.id === (DuluxEventTypes.Enquiry as Uuid) ||
            eventTemplate.id === (DuluxEventTypes.Onboarding as Uuid));
    const isScaffoldStyledEventPanel =
        isEmotoradInqueueStyledEventPanel === true ||
        isAmaronScaffoldStyledEventPanel === true ||
        isDuluxScaffoldStyledEventPanel === true;

    const isEmotoradCancelledReadOnly = useMemo(() => {
        if (isEmotoradWorkspace(workspace) !== true) {
            return false;
        }
        let statusColumnId: Uuid | null = null;
        if (isEmotoradCallsPanel === true) {
            statusColumnId = EMOTORAD__EVENT__CALLS__STATUS__COLUMN_ID;
        } else if (isEmotoradWalkinPanel === true) {
            statusColumnId = EMOTORAD__EVENT__WALKINS__STATUS__COLUMN_ID;
        } else if (isEmotoradTestridePanel === true) {
            statusColumnId = EMOTORAD__EVENT__TESTRIDES__STATUS__COLUMN_ID;
        } else if (isEmotoradSalesPanel === true) {
            statusColumnId = EMOTORAD__EVENT__SALES__STATUS__COLUMN_ID;
        }
        if (statusColumnId === null) {
            return false;
        }
        const raw = row.additionalColumnValues[statusColumnId];
        if (raw === null || raw === undefined) {
            return false;
        }
        const normalized = String(raw).trim();
        const cancelled = String(EmotoradCallStatusEnum.CANCELLED);
        return normalized === cancelled;
    }, [workspace, row.additionalColumnValues, row.id, isEmotoradCallsPanel, isEmotoradWalkinPanel, isEmotoradTestridePanel, isEmotoradSalesPanel]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [eventWithDiff, setEventWithDiff] = useState<EventWithEventContainer>(row);
    const [invalidRequiredColumnIds, setInvalidRequiredColumnIds] = useState<ReadonlySet<string>>(() => new Set());
    const emotoradGeoFetchGenerationRef = useRef(0);

    const eventTemplateForPersist = useMemo(() => {
        return eventTemplates.find((t) => t.id === row.templateId) ?? eventTemplate;
    }, [eventTemplates, eventTemplate, row.templateId]);

    useEffect(() => {
        setEventWithDiff(row);
        setInvalidRequiredColumnIds(new Set());
    }, [row.id]);

    const deps: ColumnValueDeps = {
        row,
        journeysModuleConfiguration,
        eventTemplates,
        users,
        userTemplates,
        platformUserDetailsByUserId,
        organizationalUnits,
        organizationalUnitTemplates,
        btlActivities,
        products,
        userDefinedObjectOptionsByTemplateId,
    };
    const headerDeps: ColumnValueDeps = {
        ...deps,
        row: eventWithDiff,
    };

    const customerName = processOttopilotStringTemplate(
        journeysModuleConfiguration.configuration.displayNameTemplate,
        row.eventContainer.additionalColumnValues,
    );

    const dispositionText = (() => {
        if (isEmotoradLeadPanel === true) {
            const leadRaw = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID];
            if (leadRaw !== null && leadRaw !== undefined && String(leadRaw).trim() !== "") {
                const leadId = String(leadRaw);
                const fromEnum = Object.values(emotoradLeadEventStatuses).find((s) => s.id === leadId);
                if (fromEnum !== undefined) {
                    return fromEnum.displayName;
                }
            }
            const digitalRaw = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];
            if (digitalRaw !== null && digitalRaw !== undefined && String(digitalRaw).trim() !== "") {
                const digitalId = String(digitalRaw);
                const fromEnum = Object.values(emotoradEnquiryDigitalLeadStatuses).find((s) => s.id === digitalId);
                if (fromEnum !== undefined) {
                    return fromEnum.displayName;
                }
            }
            const leadResolved = resolveColumnValue(
                getAdditionalColumns,
                {
                    id: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
                    label: "Lead Status",
                    columnId: EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                headerDeps,
            );
            if (leadResolved !== "-") {
                return leadResolved;
            }
            const digitalResolved = resolveColumnValue(
                getAdditionalColumns,
                {
                    id: EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
                    label: "Digital Lead Status",
                    columnId: EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                headerDeps,
            );
            if (digitalResolved !== "-") {
                return digitalResolved;
            }
        }
        if (eventTemplate.configuration.statusColumnConfig !== undefined) {
            return resolveColumnValue(
                getAdditionalColumns,
                {
                    id: eventTemplate.configuration.statusColumnConfig.columnId,
                    label: "Status",
                    columnId: eventTemplate.configuration.statusColumnConfig.columnId,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                headerDeps,
            );
        }
        return "-";
    })();

    const {date, time} = formatDateAndTime(row.createdAt);
    const eventDateTime = date !== "-" && time !== "-" ? `${date}, ${time}` : "-";
    const eventUtmPlatformColumn = getAdditionalColumns(eventTemplate).find((column) => column.displayName.trim().toLowerCase() === "utm platform");
    const journeyUtmPlatformColumn = getAdditionalColumns(journeysModuleConfiguration).find((column) => column.displayName.trim().toLowerCase() === "utm platform");
    const utmPlatform =
        eventUtmPlatformColumn !== undefined
            ? resolveColumnValue(
                getAdditionalColumns,
                {
                    id: eventUtmPlatformColumn.id,
                    label: eventUtmPlatformColumn.displayName,
                    columnId: eventUtmPlatformColumn.id,
                    columnType: "OttopilotEvent",
                    eventTemplateId: eventTemplate.id,
                    isAdditionalColumn: true,
                },
                headerDeps,
            )
            : journeyUtmPlatformColumn !== undefined
              ? resolveColumnValue(
                  getAdditionalColumns,
                  {
                      id: journeyUtmPlatformColumn.id,
                      label: journeyUtmPlatformColumn.displayName,
                      columnId: journeyUtmPlatformColumn.id,
                      columnType: "OttopilotEventContainer",
                      isAdditionalColumn: true,
                  },
                  headerDeps,
              )
              : "-";
    const utmPlatformResolved =
        isEmotoradLeadPanel === true && utmPlatform === "-"
            ? (() => {
                  const digitalRaw = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__DIGITAL_LEAD_STATUS__COLUMN_ID];
                  if (digitalRaw !== null && digitalRaw !== undefined && String(digitalRaw).trim() !== "") {
                      const digitalId = String(digitalRaw);
                      const fromEnum = Object.values(emotoradEnquiryDigitalLeadStatuses).find((s) => s.id === digitalId);
                      if (fromEnum !== undefined) {
                          return fromEnum.displayName;
                      }
                  }
                  const leadRaw = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID];
                  if (leadRaw !== null && leadRaw !== undefined && String(leadRaw).trim() !== "") {
                      const leadId = String(leadRaw);
                      const fromEnum = Object.values(emotoradLeadEventStatuses).find((s) => s.id === leadId);
                      if (fromEnum !== undefined) {
                          return fromEnum.displayName;
                      }
                  }
                  return "-";
              })()
            : utmPlatform;
    const leadOwnerUserId = journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId
        ? (row.eventContainer.additionalColumnValues[
              journeysModuleConfiguration.configuration.eventContainersPage.leadOwnerColumnId
          ] as Uuid | null)
        : null;
    const leadOwnerDisplayName = getUserDisplayName(leadOwnerUserId, users, userTemplates, platformUserDetailsByUserId);

    const columnGrouping = useMemo(() => {
        return getEditPanelColumnGroupingFromConfig(getAdditionalColumns, eventTemplate, eventWithDiff, isExpanded, isScaffoldStyledEventPanel);
    }, [getAdditionalColumns, eventTemplate, eventWithDiff, isExpanded, isScaffoldStyledEventPanel]);
    const columnGroupingWithTestrideScheduleRules = useMemo(() => {
        if (isEmotoradTestridePanel !== true) {
            return columnGrouping;
        }
        const getColumnRawValue = (column: EventColumnGroupWithOptionalIcon["columns"][number]): unknown => {
            if (column.columnType === "OttopilotEventContainer") {
                if (column.isAdditionalColumn === true) {
                    return eventWithDiff.eventContainer.additionalColumnValues[column.columnId as Uuid];
                }
                return (eventWithDiff.eventContainer as unknown as Record<string, unknown>)[column.columnId as string];
            }
            if (column.isAdditionalColumn === true) {
                return eventWithDiff.additionalColumnValues[column.columnId as Uuid];
            }
            return (eventWithDiff as unknown as Record<string, unknown>)[column.columnId as string];
        };
        const allColumns = columnGrouping.flatMap((group) => group.columns);
        const testRideDateColumn = allColumns.find((column) => column.label.trim().toLowerCase() === "test ride date");

        const testRideDateRaw = testRideDateColumn !== undefined ? getColumnRawValue(testRideDateColumn) : null;
        const dateText = testRideDateRaw === null || testRideDateRaw === undefined ? "" : String(testRideDateRaw).trim();
        let scheduledAtMs: number | null = null;
        if (dateText !== "") {
            const directParse = Date.parse(dateText);
            if (Number.isNaN(directParse) === false) {
                scheduledAtMs = directParse;
            }
        }
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const canEditSchedule = scheduledAtMs === null ? true : scheduledAtMs - Date.now() > twoHoursMs;

        return columnGrouping.map((group) => ({
            ...group,
            columns: group.columns.map((column) => {
                const normalizedLabel = column.label.trim().toLowerCase();
                if (normalizedLabel === "test ride date") {
                    return {
                        ...column,
                        isEditable: (column.isEditable ?? true) && canEditSchedule,
                    };
                }
                return column;
            }),
        }));
    }, [columnGrouping, eventWithDiff, isEmotoradTestridePanel]);

    const effectiveColumnGrouping = useMemo(() => {
        if (isEmotoradCancelledReadOnly !== true) {
            return columnGroupingWithTestrideScheduleRules;
        }
        return columnGroupingWithTestrideScheduleRules.map((group) => {
            return {
                ...group,
                columns: group.columns.map((column) => {
                    return {
                        ...column,
                        isEditable: false,
                    };
                }),
            };
        });
    }, [isEmotoradCancelledReadOnly, columnGroupingWithTestrideScheduleRules]);

    // Report unsaved edits to the shared row-engagement guard (no-op outside a panel).
    const [notesDirty, setNotesDirty] = useState(false);
    const columnsDirty = isFormValueDirty(
        {
            additionalColumnValues: eventWithDiff.additionalColumnValues,
            journeyAdditionalColumnValues: eventWithDiff.eventContainer.additionalColumnValues,
        },
        {
            additionalColumnValues: row.additionalColumnValues,
            journeyAdditionalColumnValues: row.eventContainer.additionalColumnValues,
        },
    );
    useReportPanelDirty(columnsDirty || notesDirty);

    const handleColumnValueChange = async (columnId: string, value: unknown, eventTemplateId: Uuid | undefined) => {
        const isEventColumn = eventTemplateId !== undefined;
        const isJourneyColumn = !isEventColumn && getAdditionalColumns(journeysModuleConfiguration).some((column) => column.id === (columnId as Uuid));

        setEventWithDiff((prev) => {
            const eventPatch: Record<string, unknown> = isEventColumn === true ? {[columnId]: value} : {};
            if (isEmotoradSalesPanel === true && isEventColumn === true && String(columnId) === String(EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID)) {
                const prevModel = prev.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL__COLUMN_ID];
                if (String(prevModel ?? "") !== String(value ?? "")) {
                    eventPatch[String(EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID)] = null;
                    eventPatch[String(EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID)] = null;
                }
            }
            if (isEmotoradSalesPanel === true && isEventColumn === true && String(columnId) === String(EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID)) {
                const prevVariant = prev.additionalColumnValues[EMOTORAD__EVENT__SALES__MODEL_VARIANT__COLUMN_ID];
                if (String(prevVariant ?? "") !== String(value ?? "")) {
                    eventPatch[String(EMOTORAD__EVENT__SALES__PREFERRED_COLOR__COLUMN_ID)] = null;
                }
            }
            if (
                isEmotoradTestridePanel === true &&
                isEventColumn === true &&
                String(columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID)
            ) {
                const dateId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID;
                const timeStr = value === null || value === undefined || value === "" ? "" : String(value).trim();
                eventPatch[String(columnId)] = timeStr === "" ? null : timeStr;
                const dateRaw = prev.additionalColumnValues[dateId] ?? prev.eventContainer.additionalColumnValues[dateId];
                const {pickerDate} = splitEmotoradTestRideStoredInstantToPickerDateAndSlot(dateRaw);
                const mergedIso = pickerDate !== null && timeStr !== "" ? combineEmotoradTestRidePickerDateAndSlotHhMmToUtcIso(pickerDate, timeStr) : null;
                if (mergedIso !== null) {
                    eventPatch[String(dateId)] = mergedIso;
                }
            }
            if (
                isEmotoradTestridePanel === true &&
                isEventColumn === true &&
                String(columnId) === String(EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID)
            ) {
                const dateId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_DATE__COLUMN_ID;
                const timeId = EMOTORAD__EVENT__TESTRIDES__TEST_RIDE_TIME__COLUMN_ID;
                const timeRaw = prev.additionalColumnValues[timeId];
                const timeStr = timeRaw === null || timeRaw === undefined || timeRaw === "" ? "" : String(timeRaw).trim();
                if (timeStr !== "" && value !== null && value !== undefined && value !== "") {
                    const {pickerDate} = splitEmotoradTestRideStoredInstantToPickerDateAndSlot(value);
                    const mergedIso = pickerDate !== null ? combineEmotoradTestRidePickerDateAndSlotHhMmToUtcIso(pickerDate, timeStr) : null;
                    if (mergedIso !== null) {
                        eventPatch[String(dateId)] = mergedIso;
                    }
                }
            }
            const updated: EventWithEventContainer = {
                ...prev,
                additionalColumnValues: {
                    ...prev.additionalColumnValues,
                    ...eventPatch,
                },
                eventContainer: {
                    ...prev.eventContainer,
                    additionalColumnValues: {
                        ...prev.eventContainer.additionalColumnValues,
                        ...(isJourneyColumn ? {[columnId]: value} : {}),
                    },
                },
            };

            const geoTargets = isEmotoradWorkspace(workspace) === true ? getEmotoradCustomerPincodeGeoTargets(columnId) : null;
            if (geoTargets === null) {
                return updated;
            }

            const pincodeText = value === null || value === undefined ? "" : String(value).trim();
            if (pincodeText === "") {
                const patches = buildEmotoradGeographyPatches(geoTargets, null);
                if (isEmotoradTestridePanel === true && String(columnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID)) {
                    patches.event[EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID] = null;
                    patches.event[EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID] = null;
                    patches.event[EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID] = null;
                }
                return {
                    ...updated,
                    additionalColumnValues: {
                        ...updated.additionalColumnValues,
                        ...patches.event,
                    },
                    eventContainer: {
                        ...updated.eventContainer,
                        additionalColumnValues: {
                            ...updated.eventContainer.additionalColumnValues,
                            ...patches.journey,
                        },
                    },
                };
            }

            return updated;
        });

        setInvalidRequiredColumnIds((prev) => {
            if (prev.size === 0) {
                return prev;
            }
            const next = new Set(prev);
            for (const group of effectiveColumnGrouping) {
                for (const col of group.columns) {
                    if (col.isRequired !== true || col.isEditable !== true) {
                        continue;
                    }
                    if (String(col.columnId) !== columnId) {
                        continue;
                    }
                    if (col.columnType === "OttopilotEvent" && eventTemplateId === col.eventTemplateId) {
                        next.delete(String(col.id));
                    }
                    if (col.columnType === "OttopilotEventContainer" && eventTemplateId === undefined) {
                        next.delete(String(col.id));
                    }
                }
            }
            return next;
        });

        const geoTargets = isEmotoradWorkspace(workspace) === true ? getEmotoradCustomerPincodeGeoTargets(columnId) : null;
        if (geoTargets === null) {
            return;
        }
        const pincodeText = value === null || value === undefined ? "" : String(value).trim();
        if (pincodeText === "") {
            return;
        }

        const gen = ++emotoradGeoFetchGenerationRef.current;
        try {
            const details = await getGeographyDetailsByPincode({
                intellsysBaseUrl,
                accessToken: heimdallUser.accessToken,
                pincode: pincodeText,
                dialCode: "+91",
            });
            if (gen !== emotoradGeoFetchGenerationRef.current) {
                return;
            }
            const patches = buildEmotoradGeographyPatches(geoTargets, details);
            if (isEmotoradTestridePanel === true && String(columnId) === String(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID)) {
                if (pincodeText !== "") {
                    patches.event[EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID] = value;
                }
                if (details !== null) {
                    if (details.state !== null) {
                        patches.event[EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID] = details.state;
                    }
                    if (details.city !== null) {
                        patches.event[EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID] = details.city;
                    }
                }
            }
            setEventWithDiff((prev) => ({
                ...prev,
                additionalColumnValues: {
                    ...prev.additionalColumnValues,
                    ...patches.event,
                },
                eventContainer: {
                    ...prev.eventContainer,
                    additionalColumnValues: {
                        ...prev.eventContainer.additionalColumnValues,
                        ...patches.journey,
                    },
                },
            }));
        } catch {
            // Geography lookup is best-effort; the pincode value was already applied above.
        }
    };

    const handleSave = async () => {
        if (!validateFields()) {
            return;
        }
        if (isEmotoradCancelledReadOnly === true) {
            return;
        }
        const requiredFailures = collectEmptyRequiredEditableColumnConfigIds(effectiveColumnGrouping, eventWithDiff);
        if (requiredFailures.size > 0) {
            setInvalidRequiredColumnIds(requiredFailures);
            showFlag({
                title: "Required fields",
                description: "Please fill in all required fields before saving.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
            return;
        }
        setInvalidRequiredColumnIds(new Set());
        setIsSubmitting(true);
        try {
            let journeyAdditionalForApi = pickAdditionalColumnValuesForKnownColumns(
                eventWithDiff.eventContainer.additionalColumnValues,
                getAdditionalColumns(journeysModuleConfiguration),
            );
            if (isEmotoradTestridePanel === true || isEmotoradCallsPanel === true) {
                const eventPurchaseTimelineColumnId =
                    isEmotoradTestridePanel === true
                        ? EMOTORAD__EVENT__TESTRIDES__PURCHASE_TIMELINE__COLUMN_ID
                        : EMOTORAD__EVENT__CALLS__PURCHASE_TIMELINE__COLUMN_ID;
                const nextPurchaseTimeline = eventWithDiff.additionalColumnValues[eventPurchaseTimelineColumnId];
                const prevPurchaseTimeline = row.additionalColumnValues[eventPurchaseTimelineColumnId];
                if (JSON.stringify(nextPurchaseTimeline) !== JSON.stringify(prevPurchaseTimeline)) {
                    journeyAdditionalForApi = {
                        ...journeyAdditionalForApi,
                        [EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID]: nextPurchaseTimeline,
                    };
                }
            }
            const journeyAdditionalBaseline = pickAdditionalColumnValuesForKnownColumns(
                row.eventContainer.additionalColumnValues,
                getAdditionalColumns(journeysModuleConfiguration),
            );
            const hasJourneyChanges = JSON.stringify(journeyAdditionalForApi) !== JSON.stringify(journeyAdditionalBaseline);

            let eventAdditionalForApi = pickAdditionalColumnValuesForKnownColumns(
                eventWithDiff.additionalColumnValues,
                getAdditionalColumns(eventTemplateForPersist),
            );
            if (
                isAmaronWorkspace(workspaceCapabilities) === true &&
                eventTemplate.id === (AmaronEventTypes.Calls as Uuid) &&
                Object.prototype.hasOwnProperty.call(
                    eventAdditionalForApi,
                    AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID,
                ) === true
            ) {
                const scheduledOnUtcIso = schedulePickerOrLegacyToUtcIso(
                    eventAdditionalForApi[AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID],
                );
                if (scheduledOnUtcIso !== null) {
                    eventAdditionalForApi = {
                        ...eventAdditionalForApi,
                        [AMARON__EVENT__CALLS__SCHEDULED_ON__COLUMN_ID]: scheduledOnUtcIso,
                    };
                }
            }
            if (isEmotoradWalkinPanel === true) {
                const journeyPt = eventWithDiff.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID];
                const eventPt = eventWithDiff.additionalColumnValues[EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID];
                const baselineJourneyPt = row.eventContainer.additionalColumnValues[EMOTORAD__EVENT_CONTAINER__PURCHASE_TIMELINE__COLUMN_ID];
                const journeyPurchaseTimelineChanged = JSON.stringify(journeyPt) !== JSON.stringify(baselineJourneyPt);
                if (journeyPurchaseTimelineChanged === true && JSON.stringify(journeyPt) !== JSON.stringify(eventPt)) {
                    eventAdditionalForApi = {
                        ...eventAdditionalForApi,
                        [EMOTORAD__EVENT__WALKINS__PURCHASE_TIMELINE__COLUMN_ID]: journeyPt as never,
                    };
                }
            }
            if (isEmotoradTestridePanel === true) {
                applyEmotoradTestRideDateTimeSplitToEventApiPayload(eventAdditionalForApi);
                const syncTestrideGeoFromJourneyIfChanged = (journeyColumnId: Uuid, eventColumnId: Uuid) => {
                    const journeyValue = eventWithDiff.eventContainer.additionalColumnValues[journeyColumnId];
                    const baselineJourneyValue = row.eventContainer.additionalColumnValues[journeyColumnId];
                    const eventValue = eventWithDiff.additionalColumnValues[eventColumnId];
                    const journeyChanged = JSON.stringify(journeyValue) !== JSON.stringify(baselineJourneyValue);
                    if (journeyChanged === true && JSON.stringify(journeyValue) !== JSON.stringify(eventValue)) {
                        eventAdditionalForApi = {
                            ...eventAdditionalForApi,
                            [eventColumnId]: journeyValue as never,
                        };
                    }
                };
                syncTestrideGeoFromJourneyIfChanged(EMOTORAD__EVENT_CONTAINER__CUSTOMER_PINCODE__COLUMN_ID, EMOTORAD__EVENT__TESTRIDES__CUSTOMER_PINCODE__COLUMN_ID);
                syncTestrideGeoFromJourneyIfChanged(EMOTORAD__EVENT_CONTAINER__CUSTOMER_STATE__COLUMN_ID, EMOTORAD__EVENT__TESTRIDES__CUSTOMER_STATE__COLUMN_ID);
                syncTestrideGeoFromJourneyIfChanged(EMOTORAD__EVENT_CONTAINER__CUSTOMER_CITY__COLUMN_ID, EMOTORAD__EVENT__TESTRIDES__CUSTOMER_CITY__COLUMN_ID);
            }
            const eventAdditionalBaseline = pickAdditionalColumnValuesForKnownColumns(row.additionalColumnValues, getAdditionalColumns(eventTemplateForPersist));
            const hasEventChanges = JSON.stringify(eventAdditionalForApi) !== JSON.stringify(eventAdditionalBaseline);
            const isDuluxScheduledCallEdit =
                isDuluxWorkspace(workspaceCapabilities) === true &&
                eventTemplate.id === (DuluxEventTypes.Calls as Uuid) &&
                isDuluxScheduledCallEvent(row) === true;
            const duluxScheduledCallScheduleChanged =
                isDuluxScheduledCallEdit === true
                    ? duluxScheduleRawToApiString(
                          eventWithDiff.additionalColumnValues[DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID],
                      ) !==
                      duluxScheduleRawToApiString(
                          row.additionalColumnValues[DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID],
                      )
                    : false;
            const hasPersistedChanges =
                isDuluxScheduledCallEdit === true
                    ? duluxScheduledCallScheduleChanged === true
                    : hasJourneyChanges === true || hasEventChanges === true;

            if (isAmaronWorkspace(workspaceCapabilities) === true && (hasJourneyChanges === true || hasEventChanges === true)) {
                const updateResult = await amaronUpdateEventAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventId: row.id,
                        templateId: row.templateId,
                        eventPatch: {
                            additionalColumnValues: eventAdditionalForApi,
                        },
                        ...(hasJourneyChanges === true
                            ? {
                                  eventContainerPatch: {
                                      additionalColumnValues: journeyAdditionalForApi,
                                      organizationalUnitId: getEventContainerOrganizationalUnitId(
                                          {additionalColumnValues: journeyAdditionalForApi},
                                          journeysModuleConfiguration,
                                      ),
                                  },
                              }
                            : {}),
                    },
                });
                if (updateResult.success === false) {
                    showFlag({
                        title: "Error",
                        description: updateResult.error.message,
                        appearance: "error",
                        isAutoDismiss: true,
                        autoDismissSeconds: 3,
                    });
                    setIsSubmitting(false);
                    return;
                }
            } else if (isDuluxScheduledCallEdit === true) {
                if (duluxScheduledCallScheduleChanged === true) {
                    const updateResult = await duluxEditScheduledCallAction({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl: intellsysBaseUrl,
                        request: {
                            workspaceId: workspace.id,
                            eventId: row.id,
                            scheduledOnIso: duluxScheduleRawToApiString(
                                eventWithDiff.additionalColumnValues[DULUX__EVENT__CALLS__SCHEDULE_DATE__COLUMN_ID],
                            ),
                        },
                    });
                    if (updateResult.success === false) {
                        showFlag({
                            title: "Error",
                            description: updateResult.error.message,
                            appearance: "error",
                            isAutoDismiss: true,
                            autoDismissSeconds: 3,
                        });
                        setIsSubmitting(false);
                        return;
                    }
                }
            } else if (isDuluxWorkspace(workspaceCapabilities) === true && (hasJourneyChanges === true || hasEventChanges === true)) {
                const updateResult = await duluxEventEditPanelSaveAction({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    request: {
                        workspaceId: workspace.id,
                        eventId: row.id,
                        templateId: row.templateId,
                        eventPatch: {
                            additionalColumnValues: eventAdditionalForApi,
                        },
                        ...(hasJourneyChanges === true
                            ? {
                                  eventContainerPatch: {
                                      additionalColumnValues: journeyAdditionalForApi,
                                      organizationalUnitId: getEventContainerOrganizationalUnitId(
                                          {additionalColumnValues: journeyAdditionalForApi},
                                          journeysModuleConfiguration,
                                      ),
                                  },
                              }
                            : {}),
                    },
                });
                if (updateResult.success === false) {
                    showFlag({
                        title: "Error",
                        description: updateResult.error.message,
                        appearance: "error",
                        isAutoDismiss: true,
                        autoDismissSeconds: 3,
                    });
                    setIsSubmitting(false);
                    return;
                }
            } else {
                if (hasJourneyChanges) {
                    const journeyResult = await updateEventContainer({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl: intellsysBaseUrl,
                        id: row.eventContainer.id,
                        templateId: row.eventContainer.templateId,
                        data: {
                            additionalColumnValues: journeyAdditionalForApi,
                        },
                        workspaceId: workspace.id,
                    });
                    if (journeyResult.success === false) {
                        showFlag({
                            title: "Error",
                            description: journeyResult.error.message,
                            appearance: "error",
                            isAutoDismiss: true,
                            autoDismissSeconds: 3,
                        });
                        setIsSubmitting(false);
                        return;
                    }
                }

                if (hasEventChanges || hasJourneyChanges) {
                    const result = await updateEvent({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl: intellsysBaseUrl,
                        eventId: row.id,
                        templateId: row.templateId,
                        // Stamp event audit metadata even when the edited fields are journey-backed.
                        data: (hasEventChanges ? eventAdditionalForApi : {}) as never,
                        workspaceId: workspace.id,
                    });

                    if (result.success === false) {
                        showFlag({
                            title: "Error",
                            description: result.error.message,
                            appearance: "error",
                            isAutoDismiss: true,
                            autoDismissSeconds: 3,
                        });
                        setIsSubmitting(false);
                        return;
                    }
                }
            }

            let latestRow = eventWithDiff;
            if (hasPersistedChanges === true) {
                const latestEventResult = await getEventsWithEventContainers({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    workspaceId: workspace.id,
                    limit: 1,
                    offset: 0,
                    filterConfiguration: {
                        revisionId: generateUuid(),
                        lineItems: [
                            {
                                columnId: "id",
                                operation: FilterOperation.equals,
                                value: row.id,
                            },
                        ],
                    },
                    sortConfiguration: {
                        revisionId: generateUuid(),
                        lineItems: [],
                    },
                    eventTemplateId: row.templateId,
                    eventTemplateAdditionalColumnIds: getAdditionalColumns(eventTemplateForPersist).map((column) => column.id),
                    eventContainerAdditionalColumnIds: getAdditionalColumns(journeysModuleConfiguration).map((column) => column.id),
                });
                if (latestEventResult.success !== true) {
                    showFlag({
                        title: "Error",
                        description: latestEventResult.error.message,
                        appearance: "error",
                        isAutoDismiss: true,
                        autoDismissSeconds: 3,
                    });
                    setIsSubmitting(false);
                    return;
                }
                const latestPersistedRow = latestEventResult.data[0];
                if (latestPersistedRow !== undefined) {
                    latestRow = latestPersistedRow;
                }
            }

            if (hasPersistedChanges === true) {
                showFlag({
                    title: "Success",
                    description: "Saved successfully",
                    appearance: "success",
                    isAutoDismiss: true,
                    autoDismissSeconds: 3,
                });
            }

            await Promise.all([
                invalidateOttopilotDataViewerQueries(queryClient, queryKeyPrefix ?? []),
                invalidateConfigDrivenWorkspaceQueries({
                    queryClient,
                    workspaceId: workspace.id,
                }),
            ]);
            onSuccess?.(latestRow);
        } catch (err) {
            showFlag({
                title: "Error",
                description: err instanceof Error ? err.message : "An unexpected error occurred",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderSections = () =>
        effectiveColumnGrouping.map((group) => {
            const content = (
                <EventDetailsEditColumns
                    columnGrouping={[group]}
                    journeysModuleConfiguration={journeysModuleConfiguration}
                    eventTemplates={eventTemplates}
                    eventWithDiff={eventWithDiff}
                    event={row}
                    onColumnValueChange={handleColumnValueChange}
                    invalidRequiredColumnIds={invalidRequiredColumnIds}
                />
            );
            if (group.icon !== undefined && group.iconColor !== undefined && group.iconBackgroundColor !== undefined) {
                return (
                    <AnankeTempCollapsibleSectionWithIcon
                        key={group.id}
                        title={group.displayName}
                        icon={group.icon}
                        iconColor={group.iconColor}
                        iconBackgroundColor={group.iconBackgroundColor}
                        initialIsOpen={group.initialIsOpen ?? true}
                        className={group.className}
                    >
                        {content}
                    </AnankeTempCollapsibleSectionWithIcon>
                );
            }
            return (
                <AnankeTempFormCollapsibleSection
                    key={group.id}
                    title={group.displayName}
                    initialIsOpen={group.initialIsOpen ?? true}
                    className={group.className}
                >
                    {content}
                </AnankeTempFormCollapsibleSection>
            );
        });

    const notesSection = (
        <AnankeTempCollapsibleSectionWithIcon
            title="Notes"
            icon={NoteIcon}
            iconColor="var(--ds-icon-accent-blue)"
            iconBackgroundColor={token("color.background.accent.teal.subtlest")}
            initialIsOpen={true}
            className="border-t border-ds-border py-ds-space-100 px-ds-space-200"
        >
            <ListNotesPanelBody
                context={{
                    eventContainerId: row.eventContainer.id,
                    eventContainer: eventWithDiff.eventContainer,
                    createScope: {
                        scopeType: "event",
                        eventId: row.id,
                        eventTemplateId: row.templateId,
                    },
                    eventContainerSourceLabel: resolvedNotesEventContainerSourceLabel,
                }}
                eventContainer={eventWithDiff.eventContainer}
                onEventContainerPatched={(patchedEventContainer) => {
                    setEventWithDiff((previous) => ({
                        ...previous,
                        eventContainer: {
                            ...previous.eventContainer,
                            ...patchedEventContainer,
                        },
                    }));
                }}
                isFullscreen={false}
                onDirtyChange={setNotesDirty}
            />
        </AnankeTempCollapsibleSectionWithIcon>
    );

    if (isExpanded === true) {
        if (isScaffoldStyledEventPanel === true) {
            return (
                <div ref={fieldsRef} className="grid grid-rows-[1fr_auto] h-full p-ds-space-200">
                    <div className="grid grid-flow-row gap-y-ds-space-250 min-h-0 overflow-y-auto pr-ds-space-050">
                        <div className="w-full px-ds-space-200 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                            <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                <div
                                    className="px-ds-space-100 py-ds-space-025 flex items-center"
                                    style={{
                                        borderRadius: "3px",
                                        border: "0.5px solid var(--ds-border-information)",
                                        background: "var(--ds-background-accent-blue-subtlest)",
                                    }}
                                >
                                    <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>{dispositionText}</span>
                                </div>
                            </div>
                            <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                                <AnankeHeading size="medium">{customerName === "" ? "-" : customerName}</AnankeHeading>
                                <div className="border border-ds-border rounded-ds-border-radius">
                                    <AnankeIconButton
                                        type="button"
                                        icon={LinkExternalIcon}
                                        label="View lead details"
                                        appearance="subtle"
                                        onClick={() => {
                                            navigate({to: configJourneyProfileHref(workspace.id, row.eventContainer.id, undefined, row.eventContainer.templateId)});
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-flow-col justify-start gap-x-ds-space-100">
                                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                                    <CalendarIcon
                                        label=""
                                        color="var(--ds-icon-accent-blue)"
                                    />
                                    <AnankeText size="small">{eventDateTime}</AnankeText>
                                </div>
                                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                                    <TagIcon
                                        label=""
                                        color="var(--ds-icon-accent-green)"
                                    />
                                    <AnankeText size="small">{utmPlatformResolved}</AnankeText>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-flow-row">
                            {renderSections()}
                            {notesSection}
                        </div>
                    </div>
                    <div className="w-full grid grid-cols-[auto] justify-end pt-ds-space-150 mt-ds-space-100 border-t border-ds-border bg-ds-surface px-ds-space-200 pb-ds-space-100">
                        <AnankeButton
                            type="button"
                            appearance="primary"
                            onClick={handleSave}
                            isLoading={isSubmitting}
                            isDisabled={isEmotoradCancelledReadOnly === true}
                        >
                            Update
                        </AnankeButton>
                    </div>
                </div>
            );
        }
        return (
            <div ref={fieldsRef} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-x-ds-space-200 p-ds-space-200">
                <div className="grid grid-flow-row gap-y-ds-space-250 pb-ds-space-200 h-fit">
                    <div className="w-full px-ds-space-200 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                            <div
                                className="px-ds-space-100 py-ds-space-025 flex items-center"
                                style={{
                                    borderRadius: "3px",
                                    border: "0.5px solid var(--ds-border-information)",
                                    background: "var(--ds-background-accent-blue-subtlest)",
                                }}
                            >
                                <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>{dispositionText}</span>
                            </div>
                        </div>
                        <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                            <AnankeHeading size="medium">{customerName === "" ? "-" : customerName}</AnankeHeading>
                            <div className="border border-ds-border rounded-ds-border-radius">
                                <AnankeIconButton
                                    type="button"
                                    icon={LinkExternalIcon}
                                    label="View lead details"
                                    appearance="subtle"
                                    onClick={() => {
                                        navigate({to: configJourneyProfileHref(workspace.id, row.eventContainer.id, undefined, row.eventContainer.templateId)});
                                    }}
                                />
                            </div>
                        </div>
                        <div className="grid grid-flow-col justify-start gap-x-ds-space-100">
                            <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                                <CalendarIcon
                                    label=""
                                    color="var(--ds-icon-accent-blue)"
                                />
                                <AnankeText size="small">{eventDateTime}</AnankeText>
                            </div>
                            <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                                <TagIcon
                                    label=""
                                    color="var(--ds-icon-accent-green)"
                                />
                                <AnankeText size="small">{utmPlatformResolved}</AnankeText>
                            </div>
                        </div>
                    </div>
                    <div className="-mx-ds-space-200 px-ds-space-400 py-ds-space-100 bg-ds-background-accent-blue-subtlest grid grid-cols-[minmax(0,1fr)_max-content] items-center">
                        <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-ds-space-100 items-center min-w-0">
                            <AnankeAvatar
                                size="small"
                                name={leadOwnerDisplayName}
                            />
                            <AnankeText>{leadOwnerDisplayName}</AnankeText>
                        </div>
                        <div
                            className="px-ds-space-100 py-ds-space-025 flex items-center"
                            style={{
                                borderRadius: "4px",
                                background: "var(--ds-icon-inverse)",
                            }}
                        >
                            <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>Assignee</span>
                        </div>
                    </div>

                    <div className="grid grid-flow-row">
                        {renderSections()}
                        {notesSection}
                    </div>

                    <div className="w-full grid grid-cols-[auto] justify-end pt-ds-space-200">
                        <AnankeButton
                            type="button"
                            appearance="primary"
                            onClick={handleSave}
                            isLoading={isSubmitting}
                            isDisabled={isEmotoradCancelledReadOnly === true}
                        >
                            Save changes
                        </AnankeButton>
                    </div>
                </div>

                {/* <div className="grid grid-flow-row gap-y-ds-space-250 pb-ds-space-200 h-fit">
                    <div className="border border-ds-border rounded-ds-border-radius-200 p-ds-space-200">
                        <AnankeTempCollapsibleSectionWithIcon
                            title="Edit information"
                            icon={InformationCircleIcon}
                            iconColor="var(--ds-icon)"
                            iconBackgroundColor="var(--ds-background-icon)"
                            initialIsOpen={true}
                        >
                            <div className="grid grid-flow-row gap-y-ds-space-100">
                                <AnankeHeading size="small">Editing event</AnankeHeading>
                                <p className="text-sm text-gray-600">
                                    Make changes to the event fields above. Click "Save changes" when you're done.
                                </p>
                            </div>
                        </AnankeTempCollapsibleSectionWithIcon>
                    </div>
                </div> */}
            </div>
        );
    } else {
        if (isScaffoldStyledEventPanel === true) {
            return (
                <div ref={fieldsRef} className="grid grid-rows-[1fr_auto] h-full pb-ds-space-200">
                    <div className="min-h-0 overflow-y-auto">
                        <div className="w-full px-ds-space-200 pt-ds-space-050 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                            <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                                <div
                                    className="px-ds-space-100 py-ds-space-025 flex items-center"
                                    style={{
                                        borderRadius: "3px",
                                        border: "0.5px solid var(--ds-border-information)",
                                        background: "var(--ds-background-accent-blue-subtlest)",
                                    }}
                                >
                                    <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>{dispositionText}</span>
                                </div>
                            </div>
                            <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                                <AnankeHeading size="medium">{customerName === "" ? "-" : customerName}</AnankeHeading>
                                <div className="border border-ds-border rounded-ds-border-radius">
                                    <AnankeIconButton
                                        type="button"
                                        icon={LinkExternalIcon}
                                        label="View lead details"
                                        appearance="subtle"
                                        onClick={() => {
                                            navigate({to: configJourneyProfileHref(workspace.id, row.eventContainer.id, undefined, row.eventContainer.templateId)});
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-flow-col justify-start gap-x-ds-space-100">
                                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                                    <CalendarIcon
                                        label=""
                                        color="var(--ds-icon-accent-blue)"
                                    />
                                    <AnankeText size="small">{eventDateTime}</AnankeText>
                                </div>
                                <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                                    <TagIcon
                                        label=""
                                        color="var(--ds-icon-accent-green)"
                                    />
                                    <AnankeText size="small">{utmPlatformResolved}</AnankeText>
                                </div>
                            </div>
                        </div>

                        <div className="w-full px-ds-space-200 py-ds-space-100 bg-ds-background-accent-blue-subtlest border-y border-ds-border grid grid-cols-[minmax(0,1fr)_max-content] items-center">
                            <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-ds-space-100 items-center min-w-0">
                                <AnankeAvatar
                                    size="small"
                                    name={leadOwnerDisplayName}
                                />
                                <AnankeText>{leadOwnerDisplayName}</AnankeText>
                            </div>
                            <div
                                className="px-ds-space-100 py-ds-space-025 flex items-center"
                                style={{
                                    borderRadius: "4px",
                                    background: "var(--ds-icon-inverse)",
                                }}
                            >
                                <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>Assignee</span>
                            </div>
                        </div>

                        <div className="w-full grid grid-flow-row">
                            {renderSections()}
                            {notesSection}
                        </div>
                    </div>

                    <div className="w-full grid grid-cols-[auto] justify-end px-ds-space-200 pt-ds-space-150 pb-ds-space-100 mt-ds-space-100 border-t border-ds-border bg-ds-surface">
                        <AnankeButton
                            type="button"
                            appearance="primary"
                            onClick={handleSave}
                            isLoading={isSubmitting}
                            isDisabled={isEmotoradCancelledReadOnly === true}
                        >
                            Update
                        </AnankeButton>
                    </div>
                </div>
            );
        }
        return (
            <div ref={fieldsRef} className="grid grid-flow-row pb-ds-space-200">
                <div className="w-full px-ds-space-200 pt-ds-space-050 pb-ds-space-150 grid grid-flow-row gap-y-ds-space-100">
                    <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-050">
                        <div
                            className="px-ds-space-100 py-ds-space-025 flex items-center"
                            style={{
                                borderRadius: "3px",
                                border: "0.5px solid var(--ds-border-information)",
                                background: "var(--ds-background-accent-blue-subtlest)",
                            }}
                        >
                            <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>{dispositionText}</span>
                        </div>
                    </div>
                    <div className="grid grid-flow-col gap-ds-space-100 items-center justify-between">
                        <AnankeHeading size="medium">{customerName === "" ? "-" : customerName}</AnankeHeading>
                        <div className="border border-ds-border rounded-ds-border-radius">
                            <AnankeIconButton
                                type="button"
                                icon={LinkExternalIcon}
                                label="View lead details"
                                appearance="subtle"
                                onClick={() => {
                                    navigate({to: configJourneyProfileHref(workspace.id, row.eventContainer.id, undefined, row.eventContainer.templateId)});
                                }}
                            />
                        </div>
                    </div>
                    <div className="grid grid-flow-col justify-start gap-x-ds-space-100">
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                            <CalendarIcon
                                label=""
                                color="var(--ds-icon-accent-blue)"
                            />
                            <AnankeText size="small">{eventDateTime}</AnankeText>
                        </div>
                        <div className="grid grid-cols-[max-content_max-content] gap-x-ds-space-100 border border-ds-border h-fit p-ds-space-050 rounded-ds-border-radius">
                            <TagIcon
                                label=""
                                color="var(--ds-icon-accent-green)"
                            />
                            <AnankeText size="small">{utmPlatformResolved}</AnankeText>
                        </div>
                    </div>
                </div>

                <div className="w-full px-ds-space-200 py-ds-space-100 bg-ds-background-accent-blue-subtlest border-y border-ds-border grid grid-cols-[minmax(0,1fr)_max-content] items-center">
                    <div className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-ds-space-100 items-center min-w-0">
                        <AnankeAvatar
                            size="small"
                            name={leadOwnerDisplayName}
                        />
                        <AnankeText>{leadOwnerDisplayName}</AnankeText>
                    </div>
                    <div
                        className="px-ds-space-100 py-ds-space-025 flex items-center"
                        style={{
                            borderRadius: "4px",
                            background: "var(--ds-icon-inverse)",
                        }}
                    >
                        <span style={{color: "var(--ds-text-information)", fontSize: "12px", fontWeight: 500}}>Assignee</span>
                    </div>
                </div>

                <div className="w-full grid grid-flow-row">
                    {renderSections()}
                    {notesSection}

                    <div className="w-full grid grid-cols-[auto] justify-end px-ds-space-200 pt-ds-space-150 pb-ds-space-100 mt-ds-space-100 border-t border-ds-border bg-ds-surface">
                        <AnankeButton
                            type="button"
                            appearance="primary"
                            onClick={handleSave}
                            isLoading={isSubmitting}
                            isDisabled={isEmotoradCancelledReadOnly === true}
                        >
                            Save changes
                        </AnankeButton>
                    </div>
                </div>
            </div>
        );
    }
}
